import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import { getOrdersUserService } from "./../../../services/userServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  createOrderAdminService,
  deleteOrderAdminService,
  getUsersAdminService,
  sendOrderSMSAdminService,
} from "../../../services/adminServices";

export const orderUserContext = createContext({
  orders: [],
  setOrders: () => {},
  order: {},
  setOrder: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  isSendSMS: "",
  setIsSendSMS: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleGetOrders: () => {},
  handleCreateOrder: () => {},
  handleSendOrderSMS: () => {},
  handleDeleteOrder: () => {},
});

const OrderUserContext = ({ children }) => {
  const dispatch = useDispatch();

  const { customer, courier } = useParams();

  const [orders, setOrders] = useState([]);
  const [order, setOrder] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [isSendSMS, setIsSendSMS] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setOrder({});
  };

  // Get Users
  const handleGetUsers = async (filter, role, callback) => {
    const objData = {
      filter,
      role,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        !!callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} ${item.lastName} - ${item.phoneNumber}`,
            })),
          );
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Orders
  const handleGetOrders = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      ...(isSendSMS && { isSendSMS }),
      ...(customer && { customer }),
      ...(courier && { courier }),
      filter,
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getOrdersUserService(objData);
      if (status === 200) {
        setOrders(data.result?.orders);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Order
  const handleCreateOrder = async (orderData) => {
    const objData = {
      ...orderData,
      customer: orderData?.customer?.value,
      courier: orderData?.courier?.value,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createOrderAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetOrders();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Send Order SMS
  const handleSendOrderSMS = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await sendOrderSMSAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetOrders();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Order
  const handleDeleteOrder = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteOrderAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetOrders();
      }
    } catch ({ response }) {
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <orderUserContext.Provider
      value={{
        orders,
        setOrders,
        order,
        setOrder,
        filter,
        setFilter,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        isSendSMS,
        setIsSendSMS,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleGetOrders,
        handleCreateOrder,
        handleSendOrderSMS,
        handleDeleteOrder,
      }}
    >
      {children}
    </orderUserContext.Provider>
  );
};

export default OrderUserContext;
